/**
 * Access Control Catalog — mirror frontend del catalogo backend.
 *
 * Resource naming convention:
 *   hub:{hubId}
 *   app:{hubId}.{appId}
 *   feature:{hubId}.{appId}.{featureId}
 *   platform:{domain}:{code}
 */

import type { Resource, ResourceKind, ResourceType, ActionType } from '@/types/access-control';
import {
  PLATFORM_PERMISSION_DEFINITIONS,
  getModulePermissionTarget,
  isSensitivePermission
} from '@/types/platform-permissions';

export const RESOURCE_TYPES: Record<string, ResourceType> = {
  HUB: 'hub',
  APP: 'app',
  FEATURE: 'feature',
  PLATFORM: 'platform'
};

export const ACTION_LABELS: Record<ActionType, string> = {
  view: 'Visualizza / Accedi',
  create: 'Crea',
  read: 'Leggi',
  update: 'Modifica',
  delete: 'Elimina',
  execute: 'Esegui',
  manage: 'Gestisci'
};

const PLATFORM_GROUPS = {
  dataset: { label: 'Dataset', items: ['dataset:read', 'dataset:ingest', 'dataset:delete', 'dataset:bridge'] },
  export: { label: 'Export', items: ['export:read', 'export:create'] },
  sensitive_modules: { label: 'Dati sensibili', items: ['business:read_sensitive'] },
  // [Dashboard granular access] "Gestione e Controllo": abilita l'intero modulo Dashboard
  // (tutti gli ambiti, tutti i CDC, tab Dettagli e Analisi) SENZA equivalere a isAdmin
  // e senza dare accesso ad altri hub/app. Va assegnato esplicitamente al gruppo.
  dashboard_control: { label: 'Dashboard - Controllo completo', items: ['business:dashboard_full_access'] }
} as const;

const RESOURCES: Record<string, Resource> = buildCatalog();

function buildCatalog(): Record<string, Resource> {
  const resources: Resource[] = [];

  // Business
  resources.push(hub('business', 'Business', 'Hub Controllo di Gestione e Analytics'));
  resources.push(app('business', 'dashboard', 'Dashboard', 'Dashboard Controllo di Gestione', { crud: false }));
  // Tab granulari della Dashboard: "view" su app:business.dashboard = accesso base
  // (solo Panoramica). Dettagli e Analisi richiedono un permesso esplicito separato,
  // oppure il flag "Gestione e Controllo" (platform:business:dashboard_full_access).
  resources.push(feature('business', 'dashboard', 'dettagli', 'Dashboard - Tab Dettagli'));
  resources.push(feature('business', 'dashboard', 'analisi', 'Dashboard - Tab Analisi'));
  // Filtro CDC: permette di restringere la vista Dashboard a un singolo CDC
  // all'interno degli ambiti già autorizzati. Puramente una funzionalità UI:
  // il perimetro dati reale è comunque sempre applicato server-side
  // (validate_requested_scope_cdc/build_perimeter_expr) indipendentemente
  // da questo permesso, che quindi non amplia mai l'accesso ai dati.
  resources.push(feature('business', 'dashboard', 'filtro_cdc', 'Dashboard - Filtra per CDC'));
  resources.push(app('business', 'personale', 'Personale', 'Gestione Personale'));
  resources.push(app('business', 'portafoglio', 'Portafoglio', 'Gestione Portafoglio'));
  resources.push(app('business', 'fornitori', 'Accesso all’anagrafica', 'Anagrafica Fornitori', { actions: ['view', 'update'] }));
  resources.push(feature('business', 'fornitori', 'gestione', 'Gestione dati e contatti'));

  // Dipendenze
  resources.push(hub('dipendenze', 'Dipendenze', 'Hub Gestione Dipendenze'));
  resources.push(app('dipendenze', 'dashboard', 'Dashboard', 'Dashboard GISSS', { crud: false }));
  resources.push(app('dipendenze', 'gestione-assistiti', 'Gestione Assistiti', 'Anagrafica e schede assistiti'));

  resources.push(app(
    'dipendenze',
    'area-sanitaria',
    'Area Sanitaria',
    'Sezione Area Sanitaria',
    { resourceKind: 'container' },
  ));
  resources.push(app('dipendenze', 'gestione-terapie', 'Gestione Terapie', 'Piani farmacologici e terapie', { parent: 'area-sanitaria' }));
  resources.push(app('dipendenze', 'gestione-ordini', 'Gestione Ordini', 'Richieste e registro ordini farmaci', {
    parent: 'area-sanitaria',
    actions: ['view', 'read', 'create', 'update'],
  }));
  resources.push(app('dipendenze', 'gestione-stdp', 'Gestione STDP', 'Elenco farmaci e consegne STDP', { parent: 'area-sanitaria', crud: false }));
  resources.push(app('dipendenze', 'gestione-mmg', 'Gestione MMG', 'Medici di Medicina Generale', { parent: 'area-sanitaria' }));

  resources.push(app(
    'dipendenze',
    'piani-terapeutici',
    'Piani Terapeutici',
    'Sezione Piani Terapeutici',
    { resourceKind: 'container' },
  ));
  resources.push(app('dipendenze', 'pt-carica-dati', 'Carica Dati', 'Upload questionari ICF', { parent: 'piani-terapeutici' }));
  resources.push(app('dipendenze', 'pt-questionari', 'Questionari', 'Gestione questionari ICF', { parent: 'piani-terapeutici' }));
  resources.push(app('dipendenze', 'pt-piani', 'Piani', 'Piani terapeutici individuali', { parent: 'piani-terapeutici' }));
  resources.push(app('dipendenze', 'pt-assistente-ia', 'Assistente IA', 'Analisi PTRI con intelligenza artificiale', { parent: 'piani-terapeutici' }));
  resources.push(feature('dipendenze', 'pt-assistente-ia', 'utilizzo-llm', 'Utilizzo LLM'));

  resources.push(app('dipendenze', 'gestione-strutture', 'Gestione Strutture', 'Configurazione sedi operative'));
  resources.push(app(
    'dipendenze',
    'info-contatti',
    'Info e Contatti Utili',
    'Guide, vademecum e contatti AST',
    { resourceKind: 'container' },
  ));

  // Documenti
  resources.push(hub('documenti', 'Documenti', 'Gestione documenti aziendali'));
  resources.push(app('documenti', 'procedures', 'Procedure', 'Procedure aziendali'));
  resources.push(app('documenti', 'forms', 'Modulistica', 'Moduli compilabili'));
  resources.push(app('documenti', 'records', 'Registrazioni', 'Registrazioni'));
  resources.push(app('documenti', 'manuals', 'Manuali', 'Manuali'));
  resources.push(app('documenti', 'regulations', 'Normative', 'Normative'));
  resources.push(app('documenti', 'search', 'Ricerca', 'Ricerca documenti'));
  resources.push(app('documenti', 'upload', 'Carica', 'Carica documenti'));
  resources.push(app(
    'documenti',
    'console',
    'Console documentale',
    'Configurazione e gestione documentale',
    { actions: ['view'] },
  ));
  resources.push(feature('documenti', 'console', 'taxonomies', 'Gestione tassonomie e termini', {
    actions: ['view', 'create', 'update', 'manage'],
  }));
  resources.push(feature('documenti', 'console', 'document-types', 'Gestione tipi documento', {
    actions: ['view', 'create', 'update', 'manage'],
  }));
  resources.push(feature('documenti', 'console', 'documents', 'Gestione documenti', {
    actions: ['create', 'update', 'manage', 'delete'],
  }));

  // Applicativi
  resources.push(hub('applicativi', 'Applicativi', 'Applicativi esterni integrati'));
  resources.push(app('applicativi', 'casa-bottega', 'Casa&Bottega', 'Gestione appartamenti'));

  resources.push(hub('hr', 'HR', 'Gestione del personale e dei cedolini'));
  resources.push(app('hr', 'soci-dipendenti', 'Soci e dipendenti', 'Anagrafica soci e dipendenti', {
    actions: ['view'],
  }));
  resources.push(app('hr', 'cedolini', 'Cedolini', 'Consultazione dei cedolini paga', {
    actions: ['view', 'read'],
  }));
  resources.push(feature('hr', 'cedolini', 'import', 'Importazione massiva', {
    actions: ['create', 'execute'],
  }));
  resources.push(feature('hr', 'cedolini', 'perimeters', 'Gestione perimetri', {
    actions: ['manage'],
  }));
  resources.push(app('hr', 'inail', 'INAIL', 'PAT Inail e voci di rischio', {
    actions: ['view', 'create', 'update', 'delete'],
  }));
  resources.push(app('hr', 'parametri', 'Parametri azienda', 'Profili di parametri aziendali per il costo orario', {
    actions: ['view', 'create', 'update', 'delete'],
  }));
  resources.push(app('hr', 'turni', 'Turni', 'Pianificazione turni di lavoro'));
  resources.push(feature('hr', 'turni', 'generazione', 'Generazione piano turni', {
    actions: ['execute'],
  }));
  resources.push(feature('hr', 'turni', 'perimeters', 'Gestione perimetri', {
    actions: ['manage'],
  }));

  // Platform: i permessi "modulo" (es. supplier:*, business:read_sensitive) sono
  // realmente consumati da un hub/app specifico (vedi authService.buildLegacyPermissions
  // e getModulePermissionTarget) e vengono quindi annidati lì nell'albero, invece di
  // apparire in una sezione "Piattaforma" scollegata dal contesto applicativo reale.
  for (const [group, { label, items }] of Object.entries(PLATFORM_GROUPS)) {
    for (const item of items) {
      const definition = PLATFORM_PERMISSION_DEFINITIONS.find((d) => d.key === item);
      const target = getModulePermissionTarget(item);
      resources.push({
        id: `platform:${item}`,
        type: 'platform',
        label: definition?.label || `${label}: ${item}`,
        description: definition?.description,
        code: item,
        group,
        hubId: target?.hub,
        appId: target?.app,
        parentId: target ? (target.app ? `app:${target.hub}.${target.app}` : `hub:${target.hub}`) : undefined,
        actions: ['execute']
      });
    }
  }

  // Data scopes: filtri per ambito della Dashboard Controllo di Gestione (business).
  // Sono consumati esclusivamente da quell'app (vedi authService.buildLegacyPermissions),
  // per questo sono annidati sotto app:business.dashboard invece di stare isolati.
  const SCOPES = [
    'AMMINISTRAZIONE', 'DIPENDENZE', '0 - 18', 'DISAGIO ADULTO', 'SENIOR',
    'MIGRANTI', 'RICEZIONE', 'DIVERSA ABILITÀ', 'AMBIENTE', 'SPORT', 'FORMAZIONE'
  ];
  for (const scope of SCOPES) {
    resources.push({
      id: `scope:${scope}`,
      type: 'scope',
      label: scope,
      description: `Filtro ambito "${scope}" per la Dashboard Controllo di Gestione`,
      hubId: 'business',
      appId: 'dashboard',
      parentId: 'app:business.dashboard',
      actions: ['view']
    });
  }

  return resources.reduce((acc, r) => {
    acc[r.id] = r;
    return acc;
  }, {} as Record<string, Resource>);
}

function hub(id: string, label: string, description: string): Resource {
  return {
    id: `hub:${id}`,
    type: 'hub',
    hubId: id,
    label,
    description,
    actions: ['view']
  };
}

function app(
  hubId: string,
  appId: string,
  label: string,
  description: string,
  { parent, crud = true, actions, resourceKind = 'application' }: {
    parent?: string;
    crud?: boolean;
    actions?: ActionType[];
    resourceKind?: ResourceKind;
  } = {}
): Resource {
  const resolvedActions = actions ?? (resourceKind === 'container'
    ? ['view']
    : crud
      ? (['view', 'create', 'read', 'update', 'delete'] as ActionType[])
      : (['view', 'read'] as ActionType[]));
  return {
    id: `app:${hubId}.${appId}`,
    type: 'app',
    resourceKind,
    hubId,
    appId,
    label,
    description,
    parentId: parent ? `app:${hubId}.${parent}` : undefined,
    actions: resolvedActions
  };
}

function feature(
  hubId: string,
  appId: string,
  featureId: string,
  label: string,
  { parent, actions = ['view'] }: { parent?: string; actions?: ActionType[] } = {}
): Resource {
  return {
    id: `feature:${hubId}.${appId}.${featureId}`,
    type: 'feature',
    hubId,
    appId,
    featureId,
    label,
    parentId: parent ? `feature:${hubId}.${appId}.${parent}` : `app:${hubId}.${appId}`,
    actions
  };
}

export function getAllResources(): Resource[] {
  return Object.values(RESOURCES);
}

export function getFullHubAccessPermissions(hubId: string): string[] {
  return getAllResources()
    .filter((resource) => (
      resource.type === 'hub' || resource.type === 'app' || resource.type === 'feature' || resource.type === 'scope'
    ) && (resource.hubId === hubId || (resource.type === 'hub' && resource.id === `hub:${hubId}`)))
    .flatMap((resource) => resource.actions.map((action) => `${resource.id}:${action}`))
    .sort();
}

export function getResource(resourceId: string): Resource | undefined {
  return RESOURCES[resourceId];
}

export function resourceExists(resourceId: string): boolean {
  return !!RESOURCES[resourceId];
}

export function getActionsForResource(resourceId: string): ActionType[] {
  const resource = getResource(resourceId);
  return resource ? [...resource.actions] : [];
}

export function getChildResources(parentId: string): Resource[] {
  return getAllResources().filter((r) => r.parentId === parentId);
}

export type PermissionCategoryKind = 'feature' | 'platform' | 'scope';

export interface PermissionCategory {
  id: PermissionCategoryKind;
  label: string;
  description: string;
  resources: Resource[];
}

export function getPermissionCategories(appResourceId: string): PermissionCategory[] {
  const categories: Array<Omit<PermissionCategory, 'resources'>> = [
    { id: 'feature', label: 'Funzionalità', description: 'Funzioni specifiche dell’applicativo.' },
    { id: 'platform', label: 'Power', description: 'Capability aggiuntive, distinte dall’accesso ordinario.' },
    { id: 'scope', label: 'Ambiti', description: 'Perimetri dati; non concedono da soli accesso all’app.' },
  ];
  const children = getChildResources(appResourceId);
  return categories
    .map((category) => ({ ...category, resources: children.filter((resource) => resource.type === category.id) }))
    .filter((category) => category.resources.length > 0);
}

export function getHubResources(): Resource[] {
  return getAllResources().filter((r) => r.type === 'hub');
}

export function getTopLevelApps(hubId: string): Resource[] {
  return getAllResources().filter(
    (r) => r.type === 'app' && r.hubId === hubId && !r.parentId
  );
}

export function getAppsByHub(hubId: string): Resource[] {
  return getAllResources().filter((r) => r.type === 'app' && r.hubId === hubId);
}

export function getFeaturesByApp(hubId: string, appId: string): Resource[] {
  return getAllResources().filter(
    (r) => r.type === 'feature' && r.hubId === hubId && r.appId === appId
  );
}

export function getPlatformResources(): Resource[] {
  return getAllResources().filter((r) => r.type === 'platform');
}

export function getPlatformGroups(): typeof PLATFORM_GROUPS {
  return PLATFORM_GROUPS;
}

export const APP_PRIVILEGE_LEVELS = ['view', 'read', 'update', 'create', 'delete'] as const;
export type AppPrivilegeLevel = typeof APP_PRIVILEGE_LEVELS[number];

export function getAppPrivilegePermissions(resourceId: string, level: AppPrivilegeLevel): string[] {
  const resource = getResource(resourceId);
  if (!resource || resource.type !== 'app') return [];
  const index = APP_PRIVILEGE_LEVELS.indexOf(level);
  return APP_PRIVILEGE_LEVELS
    .slice(0, index + 1)
    .filter((action) => resource.actions.includes(action))
    .map((action) => makePermissionString(resourceId, action));
}

export function getAppPrivilege(resourceId: string, permissions: string[]): AppPrivilegeLevel | null {
  const resource = getResource(resourceId);
  if (!resource || resource.type !== 'app') return null;
  const granted = new Set(permissions);
  const available = APP_PRIVILEGE_LEVELS.filter((action) => resource.actions.includes(action));
  return available.filter((action) => granted.has(makePermissionString(resourceId, action))).at(-1) || null;
}

/** Etichette umane dei livelli di privilegio "a scala" (view → delete), centralizzate
 * per evitare mapping duplicati/inline nei componenti (Drawer, selettori di assegnazione). */
export const PRIVILEGE_LEVEL_LABELS: Record<AppPrivilegeLevel, string> = {
  view: 'Accedi',
  read: 'Leggi',
  update: 'Modifica',
  create: 'Crea',
  delete: 'Elimina'
};

export function getPrivilegeLevelLabel(level: AppPrivilegeLevel): string {
  return PRIVILEGE_LEVEL_LABELS[level] || level;
}

/**
 * Calcola i permessi effettivi (solo per visualizzazione lato admin UI) a partire
 * dai permessi ereditati dai gruppi e dalle eccezioni individuali (allow/deny).
 * Non sostituisce il calcolo autoritativo del backend: serve a mostrare all'admin
 * un riepilogo coerente senza dover unire mentalmente le tre liste.
 */
export function computeEffectivePermissions(
  inherited: string[],
  allow: string[] = [],
  deny: string[] = []
): string[] {
  const granted = new Set(inherited);
  for (const p of allow) granted.add(p);
  const derived = deriveImpliedPermissions(Array.from(granted));
  return applyDenyPermissions(derived, deny);
}

export function makePermissionString(resourceId: string, action: ActionType | string): string {
  return `${resourceId}:${action}`;
}

export function parsePermissionString(permission: string): { resourceId: string; action: string } | null {
  if (!permission || typeof permission !== 'string') return null;
  const parts = permission.split(':');
  if (parts.length >= 4 && parts[0] === 'platform') {
    const action = parts.pop()!;
    const resourceId = parts.join(':');
    return { resourceId, action };
  }
  if (parts.length !== 3) return null;
  const [prefix, identifier, action] = parts;
  return { resourceId: `${prefix}:${identifier}`, action };
}

const LEGACY_CONTAINER_ACTIONS = new Set<ActionType>(['create', 'read', 'update', 'delete']);

export function isValidPermission(permission: string): boolean {
  const parsed = parsePermissionString(permission);
  if (!parsed) return false;
  const resource = getResource(parsed.resourceId);
  if (!resource) return false;
  return resource.actions.includes(parsed.action as ActionType)
    || (resource.resourceKind === 'container' && LEGACY_CONTAINER_ACTIONS.has(parsed.action as ActionType));
}

export function normalizePermissions(permissions: string[]): string[] {
  const set = new Set<string>();
  for (const p of permissions) {
    const n = normalizePermission(p);
    if (n) set.add(n);
  }
  return Array.from(set).sort();
}

export function normalizePermission(permission: string): string | null {
  const parsed = parsePermissionString(permission);
  if (!parsed) return null;
  const lower = `${parsed.resourceId}:${parsed.action.toLowerCase()}`;
  return isValidPermission(lower) ? lower : null;
}

export function getResourceType(resourceId: string): ResourceType | null {
  const resource = getResource(resourceId);
  return resource ? resource.type : null;
}

const ACTION_VERBS: Record<ActionType, string> = {
  view: 'visualizzare',
  create: 'creare nuovi elementi in',
  read: 'consultare',
  update: 'modificare',
  delete: 'eliminare elementi da',
  execute: 'utilizzare',
  manage: 'gestire configurazione e permessi di'
};

/**
 * Etichetta umana di un permesso (azione + risorsa), centralizzata qui per
 * evitare mapping sparsi nei componenti UI.
 */
export function getPermissionLabel(resource: Resource, action: ActionType | string): string {
  const actionLabel = ACTION_LABELS[action as ActionType] || action;
  if (resource.type === 'platform' && resource.code) {
    const definition = PLATFORM_PERMISSION_DEFINITIONS.find((d) => d.key === resource.code);
    if (definition) return definition.label;
  }
  if (resource.type === 'scope') return `Filtra ambito «${resource.label}»`;
  if (resource.type === 'hub' && action === 'view') return 'Accedi all’hub';
  if (resource.type === 'app' && action === 'view') return 'Accedi all’applicativo';
  if (resource.type === 'feature' && (action === 'view' || action === 'execute')) return 'Usa la funzionalità';
  return actionLabel;
}

/**
 * Descrizione operativa di un permesso, generata dai dati reali della risorsa
 * (mai da valori inventati). Serve a spiegare l'effetto del permesso senza
 * usare il codice tecnico come unica spiegazione.
 */
export function getPermissionDescription(resource: Resource, action: ActionType | string): string {
  const verb = ACTION_VERBS[action as ActionType] || String(action);
  const label = resource.label;

  if (resource.type === 'platform' && resource.code) {
    const definition = PLATFORM_PERMISSION_DEFINITIONS.find((d) => d.key === resource.code);
    if (definition) return definition.description;
  }
  if (resource.type === 'scope') {
    return resource.description || `Permette di filtrare i dati della dashboard per l’ambito «${label}».`;
  }
  if (resource.type === 'hub' && action === 'view') {
    return `Permette di visualizzare l’hub «${label}» e aprirne gli applicativi per cui si possiedono autorizzazioni specifiche.`;
  }
  if (resource.type === 'app' && action === 'view') {
    return `Permette di visualizzare e aprire l’applicativo «${label}».`;
  }
  if (resource.type === 'feature' && (action === 'view' || action === 'execute')) {
    return `Permette di utilizzare la funzionalità «${label}».`;
  }
  if (resource.type === 'platform') {
    return `Permette di ${verb} l’ambito «${label}».`;
  }
  return `Permette di ${verb} «${label}».`;
}

/**
 * Indica se un permesso è da considerarsi sensibile/con privilegi elevati.
 * Usa il registro reale `isSensitivePermission` per i permessi platform;
 * per le altre risorse (hub/app/feature) non esiste un flag dedicato nel
 * catalogo, quindi si applica un criterio esplicito basato sull'azione
 * (le azioni manage/delete comportano di per sé un impatto maggiore).
 */
export function isElevatedPermission(resource: Resource, action: ActionType | string): boolean {
  if (resource.type === 'platform' && resource.code) {
    return isSensitivePermission(resource.code);
  }
  return action === 'manage' || action === 'delete';
}

export function hasPermission(permissions: string[], required: string): boolean {
  if (permissions.includes('*')) return true;
  const normalized = normalizePermission(required);
  if (!normalized) return false;
  return permissions.includes(normalized);
}

export function hasAnyPermission(permissions: string[], required: string[]): boolean {
  if (!required.length) return true;
  return required.some((p) => hasPermission(permissions, p));
}

/**
 * Deriva i permessi impliciti dal modello di ereditarietà inversa:
 * se un utente può vedere un'app, deve vedere il suo hub padre;
 * se può vedere una feature, deve vedere l'app padre e l'hub.
 *
 * L'ereditarietà non funziona nel verso opposto: assegnare un hub
 * non dà accesso automatico alle app in esso contenute.
 */
function deriveParentPermissions(resource: Resource | null | undefined, result: Set<string>) {
  if (!resource || !resource.parentId) return;
  const parent = getResource(resource.parentId);
  if (!parent) return;
  result.add(makePermissionString(parent.id, 'view'));
  deriveParentPermissions(parent, result);
}

export function deriveImpliedPermissions(permissions: string[]): string[] {
  const result = new Set(permissions);
  for (const p of permissions) {
    const parsed = parsePermissionString(p);
    if (!parsed) continue;
    const resource = getResource(parsed.resourceId);
    if (!resource) continue;

    if (resource.type === 'app' && resource.hubId) {
      if (resource.resourceKind === 'container' && parsed.action !== 'view') {
        result.add(makePermissionString(resource.id, 'view'));
      }
      result.add(makePermissionString(`hub:${resource.hubId}`, 'view'));
      deriveParentPermissions(resource, result);
    } else if (resource.type === 'feature' && resource.hubId && resource.appId) {
      result.add(makePermissionString(`app:${resource.hubId}.${resource.appId}`, 'view'));
      result.add(makePermissionString(`hub:${resource.hubId}`, 'view'));
      const appResource = getResource(`app:${resource.hubId}.${resource.appId}`);
      deriveParentPermissions(appResource, result);
    }
  }
  return Array.from(result).sort();
}

function isDescendantResource(resource: Resource, ancestor: Resource): boolean {
  if (resource.id === ancestor.id) return true;
  if (ancestor.type === 'hub' && resource.hubId === ancestor.hubId) return true;

  let current: Resource | undefined = resource;
  const visited = new Set<string>();
  while (current?.parentId && !visited.has(current.id)) {
    visited.add(current.id);
    if (current.parentId === ancestor.id) return true;
    current = getResource(current.parentId);
  }
  return false;
}

/**
 * Applica i deny dopo la derivazione. Negare la view di un contenitore rende
 * non accessibili anche i suoi discendenti, evitando figli effettivi sotto un
 * parent esplicitamente negato.
 */
export function applyDenyPermissions(permissions: string[], deny: string[] = []): string[] {
  const granted = new Set(permissions);
  const legacyActions: ActionType[] = ['view', 'create', 'read', 'update', 'delete', 'execute', 'manage'];

  for (const deniedPermission of deny) {
    granted.delete(deniedPermission);
    const parsed = parsePermissionString(deniedPermission);
    if (!parsed || parsed.action !== 'view') continue;
    const deniedResource = getResource(parsed.resourceId);
    if (!deniedResource) continue;

    for (const resource of getAllResources()) {
      if (!isDescendantResource(resource, deniedResource)) continue;
      for (const action of resource.actions) granted.delete(makePermissionString(resource.id, action));
      for (const action of legacyActions) granted.delete(makePermissionString(resource.id, action));
    }
  }
  return Array.from(granted).sort();
}
